import { TFunction } from 'i18next';
import * as Sentry from '@sentry/react';
import { CodeExceptionStatus } from '../../api/models/CodeExceptionStatus';
import type { ApplyCodeExceptionResponse } from '../../api/models/ApplyCodeExceptionResponse';

/**
 * The status behind a failed `CodesService.applyCode` call, or null when the failure was not a
 * structured 400 (network drop, 500, a shape we do not recognise).
 */
export const applyCodeErrorStatus = (e: any): CodeExceptionStatus | null => {
  if (e?.status !== 400) return null;
  const code = (e?.body as ApplyCodeExceptionResponse | undefined)?.code;
  return code ?? null;
};

/**
 * Turns a failed apply-code call into something a customer can act on. Callers render this inline
 * (as `helperText` or in an Alert) -- the app has no toast system.
 *
 * This ALWAYS returns a string. The previous inline switch rethrew on anything it did not know,
 * which surfaced as an unhandled rejection and left the customer staring at a stopped spinner.
 */
export const applyCodeErrorMessage = (e: any, t: TFunction): string => {
  const status = applyCodeErrorStatus(e);
  if (status === null) {
    Sentry.captureException(e);
    return t('vouchererrors.generic');
  }

  const { detail } = e.body as ApplyCodeExceptionResponse;

  switch (status) {
    case CodeExceptionStatus.VOUCHER_STATUS_ERROR:
      return t('discounts.errors.notActive');
    case CodeExceptionStatus.ENTITY_NOT_FOUND:
      return t('vouchererrors.entitynotfound');
    case CodeExceptionStatus.CODE_NOT_FOUND:
      return t('vouchererrors.notfound');
    case CodeExceptionStatus.ORDER_NOT_MATCH_VOUCHER:
      return t('vouchererrors.missmatch');
    case CodeExceptionStatus.VOUCHER_TOO_OLD:
      return t('vouchers.expired');
    case CodeExceptionStatus.VOUCHER_USED:
      return t('vouchererrors.used');
    case CodeExceptionStatus.DISCOUNT_NOT_ACTIVE:
      return t('vouchererrors.discountNotActive');
    case CodeExceptionStatus.RETURNING_CUSTOMER_TOO_LATE:
      return t('vouchererrors.returningCustomerTooLate');
    case CodeExceptionStatus.DISCOUNT_USAGE_LIMIT_EXCEEDED:
      return t('vouchererrors.usageLimitExceeded');
    case CodeExceptionStatus.DISCOUNT_ORDER_PAGE_NOT_VALID:
      return t('vouchererrors.orderPageNotValid');
    case CodeExceptionStatus.ORDER_DATE_BEFORE_REDEMPTION_PERIOD:
      return t('vouchererrors.orderDateBeforeRedemptionPeriod');
    case CodeExceptionStatus.ORDER_DATE_AFTER_REDEMPTION_PERIOD:
      return t('vouchererrors.orderDateAfterRedemptionPeriod');
    case CodeExceptionStatus.DISCOUNT_NOT_VALID_FOR_ORDERS:
      return t('vouchererrors.discountNotValidForOrders');
    case CodeExceptionStatus.DISCOUNT_NOT_VALID_FOR_VOUCHERS:
      return t('vouchererrors.discountNotValidForVouchers');
    case CodeExceptionStatus.DISCOUNT_ALREADY_USED:
      return t('vouchererrors.onlyOncePerEmail');
    case CodeExceptionStatus.DISCOUNT_MINIMUM_AMOUNT_NOT_REACHED:
      return t('vouchererrors.discountMinimumAmountNotReached', { minAmount: detail });
    case CodeExceptionStatus.VOUCHER_LIMIT_REACHED:
      return t('vouchererrors.voucherLimitReached');
    case CodeExceptionStatus.VOUCHER_INSUFFICIENT_BALANCE:
      return t('vouchererrors.voucher_insufficient_balance');
    case CodeExceptionStatus.VOUCHER_REDEMPTION_LIMIT_REACHED:
      return t('vouchererrors.voucher_redemption_limit_reached');
    case CodeExceptionStatus.VOUCHER_ALREADY_APPLIED:
      return t('vouchererrors.voucher_already_applied');
    case CodeExceptionStatus.VOUCHER_CURRENCY_MISMATCH:
      return t('vouchererrors.voucher_currency_mismatch');
    case CodeExceptionStatus.VOUCHER_NOT_VALID_FOR_MEMBERSHIP:
      return t('vouchererrors.voucher_not_valid_for_membership');
    case CodeExceptionStatus.ORDER_NOT_AWAITING_PAYMENT:
      return t('vouchererrors.order_not_awaiting_payment');
    case CodeExceptionStatus.CODE_NOT_VALID_FOR_PAYMENT_LINK:
      return t('vouchererrors.code_not_valid_for_payment_link');
    // Covers both "this pass does not cover this activity" and a fixed-schedule membership, whose
    // sessions the studio books -- the server returns the same status for both, so the copy has to
    // hold for either.
    case CodeExceptionStatus.PASS_NOT_VALID_FOR_ORDER_PAGE:
      return t('vouchererrors.passNotValidForOrderPage');
    case CodeExceptionStatus.PASS_NOT_VALID_FOR_COURSE:
      return t('vouchererrors.passNotValidForCourse');
    case CodeExceptionStatus.PASS_STATUS_ERROR:
      return t('vouchererrors.passStatusError');
    case CodeExceptionStatus.PASS_EXPIRED:
      return t('vouchererrors.passExpired');
    case CodeExceptionStatus.PASS_NOT_STARTED:
      return t('vouchererrors.passNotStarted');
    case CodeExceptionStatus.PASS_FULLY_USED:
      return t('vouchererrors.passFullyUsed');
    case CodeExceptionStatus.PASS_SLOT_AT_CAPACITY:
      return t('vouchererrors.passSlotAtCapacity');
    default:
      Sentry.captureException(e);
      return t('vouchererrors.generic');
  }
};
